import React, { createContext, useContext } from 'react';
import { GlobalRole, User } from './models';
import { removeTokenCookies } from './utils';
import { isAdminOrSystemRole } from './roles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authMeQueryOptions, SIGNED_OUT_ME } from '@/api/auth/auth-query';
import { useSentryUser } from '@/lib/sentry/use-sentry-user';

interface RoleContextType {
  user: User | null;
  globalRole: GlobalRole | null;
  isAuthenticated: boolean;
  /** SUPERADMIN o SYSTEM: l'area `/admin` (SYSTEM in sola lettura lato API). */
  isAdminOrSystem: boolean;
  /** Solo SUPERADMIN: il backend rifiuta l'impersonation a SYSTEM. */
  canImpersonate: boolean;
  isReferrer: boolean;
  setUser: (user: User | null) => void;
  logout: () => void;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

interface RoleProviderProps {
  children: React.ReactNode;
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const { data: authData } = useQuery(authMeQueryOptions);

  const user = authData?.user ?? null;
  const globalRole = user?.globalRole ?? null;

  useSentryUser({
    userId: user?.id,
    email: user?.email,
  });

  const setUser = (newUser: User | null) => {
    queryClient.setQueryData(authMeQueryOptions.queryKey, (old) => ({
      ...old,
      authenticated: !!newUser,
      user: newUser ?? undefined,
    }));
  };

  /**
   * La voce di `/me` resta in cache, segnata non autenticata: questo
   * provider è montato fuori dalle rotte e la osserva per tutta la vita
   * dell'app. Rimossa, l'osservatore restava agganciato alla voce morta e
   * non vedeva quella scritta dal login successivo (utente senza nome e
   * ruolo sbagliato fino al reload); rileggerla subito darebbe un 401.
   */
  const logout = () => {
    removeTokenCookies();
    queryClient.setQueryData(authMeQueryOptions.queryKey, SIGNED_OUT_ME);
  };

  const value: RoleContextType = {
    user,
    globalRole,
    isAuthenticated: !!user && !!globalRole,
    isAdminOrSystem: isAdminOrSystemRole(globalRole),
    canImpersonate: globalRole === GlobalRole.SUPERADMIN,
    isReferrer: globalRole === GlobalRole.REFERRER,
    setUser,
    logout,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};
